import { FC, useEffect, useMemo, useState } from 'react';
import { Avatar, Col, Dropdown, Menu, Row } from 'antd';
import { TbInfoCircle, TbLayoutGrid } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    OurDocsLink,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { useWorkspaceStore } from '@/stores';

interface OwnerAdminWorkspaceSelectionDropdownProps {
    isPreview?: boolean;
}

const OwnerAdminWorkspaceSelectionDropdown: FC<OwnerAdminWorkspaceSelectionDropdownProps> = ({
    isPreview = false,
}): JSX.Element => {
    const [search, setSearch] = useState('');

    const { token } = useAntDesignTheme();

    const { activeWorkspaces, fetchActiveWorkspaces, selectedWorkspace } = useWorkspaceStore();

    useEffect(() => {
        void fetchActiveWorkspaces();
    }, [fetchActiveWorkspaces]);

    const filteredWorkspaces = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();

        return normalizedSearch
            ? activeWorkspaces.filter((workspace) => workspace.name?.toLocaleLowerCase().includes(normalizedSearch))
            : activeWorkspaces;
    }, [activeWorkspaces, search]);

    return (
        <Dropdown
            disabled={isPreview}
            placement='bottom'
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    footer={
                        <div
                            style={{
                                borderTop: `1px solid ${token.colorBorderSecondary}`,
                                maxWidth: 16 * 25,
                                padding: '0.5rem 0.75rem',
                            }}
                        >
                            <small style={{ color: token.colorTextTertiary }}>
                                <TbInfoCircle style={{ verticalAlign: '-0.1rem', marginRight: '0.25rem' }} />
                                Not seeing the workspace you're looking for? Only workspace owners & admins can
                                integrate Countercyclical with external services. Learn more in <OurDocsLink />
                            </small>
                        </div>
                    }
                    minWidth={16 * 25}
                    search={{
                        disabled: isPreview,
                        placeholder: 'Search Workspaces...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.25rem' }}
                >
                    <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                    <DropdownPanelMenu disabled={isPreview} selectable={false}>
                        {filteredWorkspaces.length ? (
                            filteredWorkspaces.map((workspace) => {
                                return (
                                    <Menu.Item key={workspace.id}>
                                        <DropdownOptionContent
                                            avatar={{ icon: <TbLayoutGrid size='1rem' /> }}
                                            metadata={['2 Active Members']}
                                            selected={selectedWorkspace?.id === workspace.id}
                                            title={workspace.name}
                                        />
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height={16 * 10}
                                icon={<TbLayoutGrid size='75%' style={{ color: token.colorTextTertiary }} />}
                                label={search ? 'No Matching Workspaces' : 'No Workspaces'}
                                description={
                                    search
                                        ? 'Try another workspace name.'
                                        : 'Eligible owner/admin workspaces will appear here.'
                                }
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <DropdownButton block disabled={isPreview}>
                <Row align='middle' style={{ width: '100%' }} gutter={[8, 0]}>
                    <Col flex='none' style={{ paddingLeft: 0 }}>
                        <Avatar
                            icon={<TbLayoutGrid size='1rem' style={{ color: token.colorTextTertiary }} />}
                            size='small'
                            shape='square'
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    </Col>
                    <Col flex='none'>Workspace Name</Col>
                    <Col flex='auto'></Col>
                </Row>
            </DropdownButton>
        </Dropdown>
    );
};

export default OwnerAdminWorkspaceSelectionDropdown;
